<template>
    <v-dialog fullscreen v-model="dialog" transition="dialog-bottom-transition">
        <v-card tile flat>
            <!-- TITLE -->
            <v-app-bar flat fixed color="white" class="kimsa-elevation">
                <v-row align="center" no-gutters>
                    <v-col cols="2">
                        <v-btn color="secondary" icon @click="goBack">
                            <v-icon>$left_arrow</v-icon>
                        </v-btn>
                    </v-col>
                    <v-col cols="8" class="app-title text-truncate">
                        {{ title }}
                        <div class="add-exercises text-truncate">
                            {{ $t('exercises.add_exercises') }}
                        </div>
                    </v-col>
                    <v-col cols="2" class="text-right">
                        <div v-if="carousel === setExerciseStep">
                            <v-icon>mdi-fire</v-icon>
                            {{ exercises.length }}
                        </div>
                    </v-col>
                </v-row>
            </v-app-bar>
            <v-sheet height="80" color="transparent"/>

            <v-card-text>
                <v-row>
                    <v-col cols="12" md="10" offset-md="1" xl="8" offset-xl="2">
                        <v-carousel :show-arrows="false" light hide-delimiters touchless v-model="carousel"
                                    height="auto">
                            <!-- NEW SEARCH EXERCISE CAROUSEL=0 -->
                            <v-carousel-item>
                                <!-- FILTER SEARCH -->
                                <v-row dense>
                                    <v-col cols="6">
                                        <kimsa-combobox
                                            :placeholder="$t('exercises.pick_one_category')"
                                            outlined dense
                                            :items="categoriesOptions"
                                            @change="categories = $event"
                                            @clear="onClearCategories"
                                            placeholder-class
                                            ref="categories"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <kimsa-combobox
                                            :placeholder="$t('exercises.pick_type')"
                                            outlined dense
                                            :readonly="!categories"
                                            :items="typesOptions"
                                            @change="types = $event"
                                            @clear="onClearTypes"
                                            placeholder-class
                                            multiple-with-chips
                                            :show-close-button="!!categories"
                                            ref="types"
                                        />
                                    </v-col>
                                    <v-col cols="12" class="pt-0">
                                        <kimsa-text-field
                                            :placeholder="$t('common.search')"
                                            :value="search"
                                            @change="search = $event"
                                            @click:append="sendTextQuery"
                                            icon="mdi-magnify"
                                            outlined dense
                                            clearable no-elevation
                                        />
                                    </v-col>
                                </v-row>

                                <!-- MESSAGES -->
                                <div v-if="exercisesOptions.length === 0" class="text-center pt-4">
                                    <v-sheet height="30" color="transparent"/>
                                    <small v-if="firstSearch">{{ $t('exercises.write_to_search_or_pick') }}</small>
                                    <small v-else>{{ $t('common.not_found') }}</small>
                                </div>

                                <!-- LIST OF EXERCISES-->
                                <v-virtual-scroll
                                    class="mt-3"
                                    :bench="8"
                                    :items="exercisesOptions"
                                    :height="exercisesHeightContainer"
                                    width="100%"
                                    item-height="80"
                                >
                                    <template v-slot:default="{ item, index }">
                                        <add-exercise-card :exercise="item" :added="isExerciseAdded(item.objectID)"
                                                           @add="addExercise($event)" :one-exercise="oneExercise"/>
                                        <v-divider v-if="exercisesOptions.length > index+1"/>
                                    </template>
                                </v-virtual-scroll>
                            </v-carousel-item>
                            <!-- EXERCISE CREATOR CAROUSEL=1 -->
                            <v-carousel-item>
                                <exercise-creator
                                    ref="creator"
                                    @success="onCreatorSuccess"
                                    @cancel="onCreatorCancel"
                                />
                            </v-carousel-item>
                        </v-carousel>
                    </v-col>
                </v-row>
            </v-card-text>

            <!-- BOTTOMS BUTTONS -->
            <v-row class="bottom-container" v-if="carousel === 0">
                <v-col cols="12" class="text-right" v-if="!oneExercise">
                    <v-btn :disabled="!isValid" color="primary" class="black--text normal-btn mr-1 mb-1" depressed
                           @click="carousel === setExerciseStep ? setExercises() : continueStepper()"
                           :block="isMobile">
                        {{ continueLabel }}
                    </v-btn>
                    <v-btn v-if="carousel <= setExerciseStep" class="normal-btn mb-1" :block="isMobile"
                           @click="carousel = 1" outlined color="black">
                        <v-icon left>mdi-plus</v-icon>
                        {{ $t('common.create') }} {{ $t('common.new') }} {{ $tc('exercises.exercises', 1) }}
                    </v-btn>
                </v-col>
            </v-row>

        </v-card>
    </v-dialog>
</template>

<script>

import KimsaSelect from '@/components/common/inputs/KimsaSelect'
import AddExerciseCard from '@/components/coach/routines/AddExerciseCard'

import {mapState, mapMutations, mapActions} from 'vuex'

import {debounce} from '@/utils/Common'
import {db} from '@/utils/Firebase'
import _map from 'lodash/map'
import {Client, ExerciseIndex, UserIndex} from '@/utils/Algolia'
import ExerciseCreator from "@/components/coach/routines/ExerciseCreator";
import ExerciseTypesPicker from "@/components/coach/routines/ExerciseTypesPicker";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";

export default {
    props: {
        title: {
            type: String,
            default() {
                return this.$tc('common.blocks', 1)
            }
        },
        block: {
            type: [Object],
        },
        oneExercise: {
            type: Boolean,
            default: false,
        },
        defaultFilter: {
            type: [Object],
        }
    },
    components: {
        KimsaTextField,
        KimsaCombobox,
        ExerciseTypesPicker,
        ExerciseCreator,
        KimsaSelect,
        AddExerciseCard,
    },
    data() {
        return {
            firstSearch: true,
            loadingSearch: false,
            dialog: false,
            carousel: 0,
            lastCarousel: 0,
            setExerciseStep: 0,
            search: '',
            categories: [],
            types: [],
            exercises: [],
            exercisesOptions: [],
            type: '',
        }
    },
    computed: {
        ...mapState(['dialogActive', 'categoriesOptions']),
        continueLabel(vm = this) {
            return vm.carousel === vm.setExerciseStep ?
                vm.$t('exercises.add_number_exercises', {number: vm.exercises.length}) :
                vm.$t('common.continue')
        },
        isValid() {
            const vm = this
            if (vm.carousel === 1 && vm.types.length === 0) return false
            if (vm.carousel === vm.setExerciseStep && vm.exercises.length === 0) return false
            return true
        },
        typesOptions(vm = this) {
            if (vm.categories === undefined || vm.categories.length === 0) return []
            return vm.categories.types.map(type => ({name: type, value: type}))
        },
        exercisesHeightContainer(vm = this) {
            if (vm.isMobile) return vm.$vuetify.breakpoint.height - 310
            return vm.$vuetify.breakpoint.height - 270
        },
        isCompany(vm = this) {
            return vm.account?.accountType === 'company'
        },
    },
    async mounted() {
        const vm = this
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        ...mapActions(['fetchExerciseCategories']),
        onTypesChange(types) {
            const vm = this
            vm.types = types
        },
        goBack() {
            const vm = this
            if (vm.carousel === 0) return vm.hide()
            if (vm.carousel > vm.setExerciseStep) return vm.$refs.creator.goBack()
            if (vm.lastCarousel !== null) return vm.carousel = vm.lastCarousel
            vm.carousel--
        },
        onCreatorCancel() {
            const vm = this
            if (vm.lastCarousel !== null) return vm.carousel = vm.lastCarousel
            vm.carousel--
        },
        onCreatorSuccess(exercise) {
            const vm = this
            console.log('exercise CREATED', exercise)
            vm.addExercise(exercise)
            let existingExerciseIndex = vm.exercisesOptions.findIndex(ex => ex.objectID === exercise.objectID)
            if (existingExerciseIndex === -1) vm.exercisesOptions.push(exercise)
            vm.carousel = vm.setExerciseStep
            vm.$refs.creator.reset()
        },
        show() {
            const vm = this
            vm.dialog = true
            vm.onBackPress(vm.goBack)
            vm.setDialogActive(true)
            vm.$nextTick(() => {
                if (vm.defaultFilter) vm.setDefaultFilter()
            })
        },
        setDefaultFilter() {
            const vm = this
            let category = vm.categoriesOptions.find(category => category.name === vm.defaultFilter.categories)
            let types = vm.defaultFilter.types.map(type => ({name: type, value: type}))
            vm.$refs.categories.model = category
            vm.$refs.types.model = types
        },
        hide() {
            const vm = this
            vm.reset()
            vm.dialog = false
            vm.setDialogActive(false)
            vm.clearOnBack()
        },
        reset() {
            const vm = this
            vm.carousel = 0
            vm.search = ''
            vm.categories = []
            if (vm.dialog) {
                vm.$nextTick(() => {
                    vm.$refs.categories.model = ''
                    vm.$refs.types.model = ''
                })
            }
            vm.types = []
            vm.exercises = []
            vm.exercisesOptions = []
        },
        continueStepper() {
            const vm = this
            return vm.carousel++
        },
        setExercises() {
            const vm = this
            vm.$emit('change', vm.exercises.slice())
            vm.hide()
            vm.reset()
        },
        debouncedLoadExercises: debounce(
            function () {
                this.loadExercises()
            }, 500),
        sendTextQuery() {
            const vm = this
            if (!vm.search) return
            vm.loadExercises()
            vm.carousel = vm.setExerciseStep
        },
        async loadExercises() {
            let vm = this
            vm.exercisesOptions = []
            vm.firstSearch = false
            vm.loadingSearch = true

            let filterCompany = ''
            if (vm.account.accountType === 'coach_company' || vm.isCompany) {
                filterCompany = `OR companyId: ${vm.account?.coachSubscription?.companyId || vm.currentUser.uid}`
            }
            let filter = `(coachId: ${vm.currentUser.uid} ${filterCompany} OR owner:"app") AND hide!=1`
            if (vm.types && vm.types.length > 0) {
                let types = vm.types.map(type => type.name)
                filter += ` AND (types:"${types.join('" OR types:"')}")`
            } else {
                // return console.log('Sin tipo seleccionado')
            }

            console.log('searching by:', vm.search, filter)
            await ExerciseIndex.search(vm.search, {
                filters: filter,
                hitsPerPage: 10000,
            })
                .then(({hits}) => {
                    // console.log('hits', hits)
                    vm.exercisesOptions = hits
                })
                .catch(err => {
                    console.log(err);
                })

            vm.loadingSearch = false
        },
        addExercise(exercise) {
            const vm = this

            if (vm.oneExercise) {
                vm.exercises.push(exercise)
                return vm.setExercises()
            }

            let added = false
            vm.exercises.forEach((ex, i) => {
                if (ex.objectID === exercise.objectID) {
                    vm.exercises.splice(i, 1)
                    added = true
                }
            })
            if (!added) vm.exercises.push(exercise)
        },
        isExerciseAdded(id) {
            const vm = this
            let added = false
            vm.exercises.forEach(ex => {
                if (ex.objectID === id) added = true
            })
            return added
        },
        onClearCategories() {
            const vm = this
            vm.categories = []
            vm.types = []
            vm.debouncedLoadExercises()
        },
        onClearTypes() {
            const vm = this
            vm.types = []
        },
    },
    watch: {
        carousel(val, oldVal) {
            const vm = this
            vm.lastCarousel = !isNaN(Number(oldVal)) && oldVal < val ? Number(oldVal) : null
        },
        types(val) {
            const vm = this
            if (val && val.length > 0) {
                vm.debouncedLoadExercises()
            } else {
                vm.exercises = []
                vm.exercisesOptions = []
            }
        },
        dialog(val) {
            if (!val) this.$emit('close')
        },
        dialogActive(val) {
            if (!val) this.hide()
        },
        search() {
            this.debouncedLoadExercises()
        },
        categories() {
            const vm = this
            if (vm.dialog) {
                vm.$nextTick(() => {
                    vm.$refs.types.model = ''
                })
            }
            vm.types = []
        },
    }
}

</script>

<style scoped>

.app-title {
    font-size: 17px;
    text-align: center;
    font-weight: 700;
}

.add-exercises {
    color: var(--v-secondary-base);
    font-size: 12px;
    font-weight: 700;
}

.bottom-container {
    margin-top: 40px;
    background: #FFFFFF;
    border-top: 1px solid #333333;
    position: fixed;
    right: 0;
    left: 0;
    bottom: 0;
    padding: 8px 16px 12px 16px;
    z-index: 1;
}

.v-divider {
    margin-left: 40%;
    margin-right: 40%;
    color: #c5c5c5;
}

</style>